import { useI18n } from 'app/components/localization';
import Dropdown from 'app/components/presentation/search/Dropdown';
import {
  AUTOMATIC_DEVICE_ID,
  DeviceInfo,
  DeviceType,
  GetDeviceManager,
  GetDevices,
  IsDeviceManagerBusy,
} from 'app/containers/rtc/DeviceManagerContext';
import { List, OrderedMap } from 'immutable';
import React, { FunctionComponent, useMemo } from 'react';

export { DeviceType };

interface ISwitchDeviceProps {
  busy: boolean;
  changeDevice: (id: string) => void;
  current?: string;
  devices: List<DeviceInfo>;
  type: DeviceType;
}

const SwitchDevice: FunctionComponent<ISwitchDeviceProps> = ({ busy, changeDevice, current, devices, type }) => {
  const { t } = useI18n();
  const [optionIds, optionLabels, renderOption] = useMemo(() => {
    const kind = type === DeviceType.Audio ? 'audioinput' : 'videoinput';

    // Determines if the current used device has been disconnected.
    const disconnectedId = devices.every(({ id }) => id !== current) ? current : undefined;

    const optionIdsToOptionLabelsInit: List<[string, string]> = devices.map(({ label, id }, index) => {
      let optionLabel;

      if (label === AUTOMATIC_DEVICE_ID) {
        optionLabel = t('rtc.devices.automatic');
      } else if (label) {
        optionLabel = label;
      } else if (kind === 'videoinput') {
        optionLabel = t('rtc.devices.generic-camera', { id: index });
      } else if (kind === 'audioinput') {
        optionLabel = t('rtc.devices.generic-microphone', { id: index });
      } else {
        optionLabel = t('rtc.devices.unknown');
      }

      return [id, optionLabel];
    });

    const optionIdsToOptionLabel = OrderedMap(optionIdsToOptionLabelsInit).asMutable();

    // Add disconnected ID in the event the option is not present.
    if (disconnectedId) {
      const optionLabel = kind === 'audioinput' ?
        t('rtc.devices.disconnected-microphone') :
        t('rtc.devices.disconnected-camera');
      optionIdsToOptionLabel.set(disconnectedId, optionLabel);
    }

    return [
      optionIdsToOptionLabel.keySeq().toList(),
      optionIdsToOptionLabel.valueSeq().toList(),
      (optionId: string) => {
        return optionIdsToOptionLabel.get(optionId) || t('rtc.devices.unknown');
      },
    ];
  }, [current, devices, t, type]);

  return (
    <Dropdown
      disabled={busy}
      onSelect={changeDevice}
      optionIds={optionIds}
      optionLabels={optionLabels}
      renderOption={renderOption}
      selectedId={current}
    />
  );
};

const SwitchDeviceInjectDependencies = ({ type }: { type: DeviceType }) => (
  <GetDeviceManager>{(deviceManager) =>
    <GetDevices>{(devices) => {
      if (!deviceManager || !devices) return null;

      const { availableDevices, currentDeviceId } = type === DeviceType.Audio ? devices.audio : devices.video;
      const changeDevice = type === DeviceType.Audio
        ? deviceManager.setPreferredAudioDeviceId
        : deviceManager.setPreferredVideoDeviceId;

      return (
        <IsDeviceManagerBusy>{(busy) =>
          <SwitchDevice
            busy={busy}
            changeDevice={changeDevice}
            current={currentDeviceId}
            devices={availableDevices}
            type={type}
          />
        }</IsDeviceManagerBusy>
      );
    }}</GetDevices>
  }</GetDeviceManager>
);

export default SwitchDeviceInjectDependencies;
